import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { EditMetricDto, FetchError, MetricAnnotationDto, MetricDatasetDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  guid: string;
  title: string;
  description: string;
  assignedTeamSlug?: string;
  reportingUserGuid?: string;
  sortOrder: string;
  datasets: MetricDatasetDto[];
  annotations?: MetricAnnotationDto[];
};

const useEditMetricRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editMetricRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditMetricDto = {
      title: variables.title,
      description: variables.description,
      assignedTeamSlug: variables.assignedTeamSlug,
      reportingUserGuid: variables.reportingUserGuid,
      sortOrder: variables.sortOrder,
      datasets: variables.datasets,
      annotations: variables.annotations,
    };

    const response = await fetch(`${configuration.apiRootUrl}/metrics/${variables.guid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editMetricRequest };
};

const useEditMetric = () => {
  const { editMetricRequest } = useEditMetricRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editMetricRequest,
    onSuccess: async (_, variables) => {
      if (variables.assignedTeamSlug) {
        await queryClient.refetchQueries({ queryKey: queryKeys.teams.getTeam(variables.assignedTeamSlug) });
      }
    },
    onSettled: async (_data, _, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.metrics.getMetric(variables.guid) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.metrics.getMetricCollections() });
    },
  });
};

export { useEditMetric };
